import React, { Component } from 'react';
import { min, max, scaleLinear, axisRight, axisBottom, select, line, area, format, utcFormat, bisector, curveLinear } from 'd3';
import { utcDay, utcMonday, utcMonth, utcYear } from 'd3';
import { createSelector } from 'reselect';
import { DATE } from './constants';
import Tooltip from './Tooltip';
import { mainColor, highlightColor, volumeBarColor } from './pallette';
import './Timeline.css';

// Area trendline and volume bars are only plotted for the main,
// non-benchmark dimension
const FIRST = 0

class Timeline extends Component {
  constructor() {
    super()

    this.state = {
      gradientId: 'areaGradient' + String(Math.random()).substr(2, 5)
    }

    this.yExtent = createSelector(
      props => props.data,
      props => props.yDimension,
      (data, yDimension) => [
        min(data, d => min(d[yDimension])),
        max(data, d => max(d[yDimension]))
      ]
    )


    this.xScale = createSelector(
      props => props.data,
      props => props.width - props.margin.l - props.margin.r,
      (data, width) =>
        scaleLinear()
          .domain([0, data.length - 1])
          .range([0, width])
    )
    this.yScale = createSelector(
      this.yExtent,
      props => props.height - props.margin.t - props.margin.b,
      props => props.yStartFromZero,
      (yExtent, height, yStartFromZero) => 
        scaleLinear()
          .domain([
            yStartFromZero ? Math.min(0, yExtent[0]) : yExtent[0],
            yStartFromZero ? Math.max(0, yExtent[1]) : yExtent[1]
          ])
          .range([height, 0])
          .nice()
    )
    this.areaString = createSelector(
      this.xScale,
      this.yScale,
      props => props.data,
      props => props.yDimension,
      props => props.curveInterpolation,
      (xScale, yScale, data, yDimension, curveInterpolation) => 
        area()
          .curve(curveInterpolation || curveLinear)
          .defined(d => d[yDimension][FIRST] !== null)
          .x((d, i) => xScale(i))
          .y0(d => yScale.range()[0])
          .y1(d => yScale(d[yDimension][FIRST]))(data)
    )
    this.lineStringBars = createSelector(
      this.xScale,
      this.yScale,
      props => props.data,
      props => props.yDimension,
      (xScale, yScale, data, yDimension) => {
        const heights = data.map(d => d[yDimension][FIRST])
        const positivePaths = heights.map(
          (h, i) => h > 0 ? `M${xScale(i)},${yScale(0)} l0,${yScale(h) - yScale(0)}` : ''
        ).join('')
        const negativePaths = heights.map(
          (h, i) => h < 0 ? `M${xScale(i)},${yScale(0)} l0,${yScale(h) - yScale(0)}` : ''
        ).join('')
        return [positivePaths, negativePaths]
      }
    )
    this.lineStrings = createSelector(
      this.xScale,
      this.yScale,
      props => props.data,
      props => props.yDimension,
      props => props.curveInterpolation,
      (xScale, yScale, data, yDimension, curveInterpolation) => 
        data[0][yDimension].map((_, i) => 
          line()
            .curve(curveInterpolation || curveLinear)
            .defined(d => d[yDimension][i] !== null)
            .x((d, i) => xScale(i))
            .y(d => yScale(d[yDimension][i]))(data)
        )
    )


    this.indexAtMouse = createSelector(
      props => props.data,
      props => props.mouseDate,
      (data, mouseDate) => {
        if (mouseDate === null) { return data.length - 1 }
        const bisectDate = bisector((d, x) => d[DATE] - x).left
        const index = bisectDate(data, mouseDate)
        return index
      }
    )


    this.ticks = createSelector(
      props => props.data,
      data => {
        const timeBoundsSpan = data[data.length - 1][DATE] - data[0][DATE]
        let interval, tickFormat
        if(timeBoundsSpan <= 7 * 8.64e7) { // ~ 7 days
          interval   = utcDay
          tickFormat = utcFormat("%-m/%-d/%y")
        }
        else if(timeBoundsSpan <= 31 * 8.64e7) { // ~ 1mo
          interval   = utcMonday
          tickFormat = utcFormat("%-m/%-d/%y")
        }
        else if(timeBoundsSpan <= 3 * 31 * 8.64e7) { // ~ 3mo
          interval   = utcMonth
          tickFormat = utcFormat("%b '%y")
        }
        else if(timeBoundsSpan <= 9 * 31 * 8.64e7) { // ~ 9mo
          interval   = utcMonth
          tickFormat = utcFormat("%b\n '%y")
        }
        else if(timeBoundsSpan <= 365 * 8.64e7) { // ~ 1yr
          interval   = utcMonth.every(3)
          tickFormat = utcFormat("%b %y")
        }
        else if(true || timeBoundsSpan <= 365 * 8.64e7) { // < 1yr
          interval   = utcYear
          tickFormat = utcFormat("%Y")
        }

        const tickData = data.reduce((ticks, d, i) => {
          const lastTickDatum = ticks[ticks.length - 1]
          if (
            ticks.length === 0 || 
            Number(interval.floor(d[DATE])) !== Number(interval.floor(lastTickDatum[DATE]))
          ) {
            ticks.push({[DATE]:d[DATE] , i})
          }
          return ticks
        }, [])

        const date0 = tickData[0][DATE]
        const fractionWithinInterval = (date0 - interval.floor(date0)) / 
                                       (interval.ceil(date0) - interval.floor(date0))

        if (fractionWithinInterval > .1) { tickData.shift() }

        return {
          tickFormat: i => tickFormat(data[i][DATE]),
          tickIndices: tickData.map(d => d.i)
        }
      }
    )
  }
  componentDidMount() {
    this.updateAxes()
  }
  componentDidUpdate() {
    this.updateAxes()
  }

  updateAxes() {
    const { width, height, margin, yFormat } = this.props
    const { tickIndices, tickFormat } = this.ticks(this.props)
    const xAxis = axisBottom(this.xScale(this.props))
      .tickValues(tickIndices)
      .tickFormat(tickFormat)
      .tickSize(-(height - margin.t - margin.b))
      .tickPadding(8)
    const yAxis = axisRight(this.yScale(this.props))
      .tickSize(-(width - margin.l - margin.r))
      .tickFormat(format(yFormat))
      .tickPadding(8)
      .ticks(4)
    select(this.refs.xaxis)
      .call(xAxis)    
    select(this.refs.yaxis)
      .call(yAxis)
  }

  handleMouseMove(e) {
    const { margin, onMouseDate, data } = this.props
    const mouseX = e.clientX - this.refs.svg.getBoundingClientRect().left - margin.l
    const mouseIndex = this.xScale(this.props).invert(mouseX)
    const datum = data[ Math.max(0, Math.min(data.length - 1, Math.round(mouseIndex))) ]
    onMouseDate(datum[DATE])
  }

	render() {
    const { width, height, margin, yDimension, data, mouseDate, onMouseDate, colorScale, barColors, 
            title, alwaysHighlightLatest, displayBottomTickValues, yFormat, renderAsBars } = this.props
    const { gradientId } = this.state

    const barWidth = renderAsBars ? Math.min(7, Math.max(1, .6 * (width / data.length))) : null
    const yScale = this.yScale(this.props)

    const mouseDateIndex = this.indexAtMouse(this.props)
    const mouseDateX = this.xScale(this.props)(mouseDateIndex) + margin.l
    const mouseDateValue = data[mouseDateIndex][yDimension][FIRST]
    const mouseDateY = yScale(mouseDateValue) + margin.t

    const hasBenchmarks = data[mouseDateIndex][yDimension].length > 1

    let tooltips
    if (!hasBenchmarks) {
      const latest = data[data.length - 1][yDimension][FIRST]
      const latestY = this.yScale(this.props)(latest) + margin.t
      tooltips = [
        alwaysHighlightLatest ? { value: latest, y: latestY, color: mainColor} : null,
        mouseDate ? { value: mouseDateValue, y: mouseDateY, color: highlightColor} : null
      ].filter(d => d !== null)
    }
    else {
      tooltips = data[mouseDateIndex][yDimension]
        .map(
          (value, i) => ({
            value,
            y: this.yScale(this.props)(value) + margin.t,
            color: colorScale(i)
          })
        )
        .filter(({value}) => value !== null)
    }

		return (<div className='Timeline'>
      <div className='Timeline-title' x={margin.l} y={margin.t - 4}>{title || ''}</div>
			<svg 
        ref='svg'
        width={width} 
        height={height}
        style={{marginLeft:-margin.l}}
        onMouseLeave={() => onMouseDate(null)}
        onMouseMove={this.handleMouseMove.bind(this)}
      >
        <defs>
          <linearGradient id={gradientId} x1='0' x2='0' y1='0' y2='1'>
            <stop className='area-gradient-top'    offset='0%'   />
            <stop className='area-gradient-bottom' offset='70%' />
          </linearGradient>
        </defs>
        { 
          renderAsBars
          ? this.lineStringBars(this.props).map((pathStr, i) => (
              <path
                key={i}
                className='Timeline-bars'
                d={pathStr}
                stroke={barColors[i]}
                strokeWidth={barWidth}
                transform={`translate(${margin.l},${margin.t})`}
              />
            ))
          : <path
              className='Timeline-area'
              d={this.areaString(this.props)}
              transform={`translate(${margin.l},${margin.t})`}
              fill={`url(#${gradientId})`}
            />
        }
        <g
          className={`Timeline-x-axis ${displayBottomTickValues ? '' : 'hide-tick-values'}`}
          ref='xaxis'
          transform={`translate(${margin.l},${height-margin.b})`}
        />
        <g
          className='Timeline-y-axis'
          ref='yaxis'
          transform={`translate(${width-margin.r},${margin.t})`}
        />
        { 
          renderAsBars
          ? null
          : this.lineStrings(this.props).map((d, i) => <g key={i}>
              <path
                className='Timeline-line'
                d={d} 
                transform={`translate(${margin.l},${margin.t})`}
                stroke={i === 0 ? 'none' : 'white'}
                strokeWidth='4'
              />,
              <path
                className='Timeline-line'
                d={d} 
                transform={`translate(${margin.l},${margin.t})`}
                stroke={colorScale(i)}
                strokeWidth='1.5'
              />
            </g>
          )
        }
        {
          tooltips.map((tooltip, i) => <Tooltip
            key={i}
            color={tooltip.color}
            label={format(yFormat)(tooltip.value)}
            x={width - margin.r + 6}
            y={tooltip.y}
            width={margin.r - 6}
          />) 
        }
        {
          mouseDate ? <g>
            <line
              className='Timeline-mouse-marker'
              x1={mouseDateX}
              x2={mouseDateX}
              y1={!displayBottomTickValues ? margin.t : 0}
              y2={displayBottomTickValues ? height - margin.b : height}
            />
            { 
              renderAsBars
              ? <line
                  className='Timeline-mouse-bar'
                  x1={mouseDateX}
                  x2={mouseDateX}
                  y1={mouseDateY}
                  y2={yScale(0) + margin.t}
                  strokeWidth={Math.max(4, barWidth + 2)}
                /> 
              : tooltips.map((tooltip, i) => !hasBenchmarks && i === 0 ? null : <g
                  key={i}
                  transform={`translate(0, ${tooltip.y})`}
                  className='Timeline-mouse-marker-group'
                >
                  <line
                    className='Timeline-mouse-marker'
                    y1={0}
                    y2={0}
                    x1={margin.l}
                    x2={width - margin.r}
                  />,
                  <circle
                    className='Timeline-mouse-circle'
                    cx={mouseDateX}
                    cy={0}
                    r={6}
                    fill={tooltip.color}
                  />
                </g>
              )
            }
          </g> : null
        }
      </svg>
    </div>)
	}
}

Timeline.defaultProps = {
  onMouseDate: function() { /* no-op by default */ },
  margin: { t:10, r:80, b:30, l:30 },
  yFormat: '.3f',
  renderAsBars: false,
  colorScale: () => mainColor,
  barColors: [volumeBarColor, volumeBarColor], // positive & negative bar colors (when renderAsBars is true)
  yStartFromZero: false,
  displayBottomTickValues: true,
  alwaysHighlightLatest: true // true means show tooltip even when mouse isn't over chart
}

export default Timeline


// WEBPACK FOOTER //
// ./src/Timeline.js